/* eslint-disable id-length */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './graphView.module.scss';
import Chart from '../../../../Components/Chart/chart';

const GraphView = ({ projectDetails }) => {
  const [fontSize, setFontSize] = useState(14);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setFontSize(width > 1600 ? 16 : 14);
    };
    handleResize(); // Set initial font size based on current window width
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!projectDetails?.overviewColumns || !projectDetails.overviewData) {
    return null;
  }

  const xAxisLabels = projectDetails.overviewColumns
    .map((column) => column.field);

  // Build a lookup from field name → { value, color }
  const valueMap = {};
  for (const item of projectDetails.overviewData) {
    let color = '#B39DDB';
    let fieldKey = null;
    let fieldValue = 0;
    for (const key in item) {
      if (key === 'statusColour') {
        color = item[key];
      } else {
        fieldKey = key;
        fieldValue = item[key];
      }
    }
    if (fieldKey !== null) {
      valueMap[fieldKey] = { value: fieldValue, color };
    }
  }

  const values = xAxisLabels.map((label) => valueMap[label]?.value ?? 0);
  const barColor = xAxisLabels.map((label) => valueMap[label]?.color ?? '#B39DDB');
  const maxValue = Math.max(...values, 1);

  const chartData = {
    labels: xAxisLabels,
    datasets: [
      {
        label: '',
        data: values,
        backgroundColor: barColor,
        barThickness: 16,
        borderRadius: 20,
        borderSkipped: 'bottom',
        minBarLength: 6,
        borderColor: 'transparent',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    animation: {
      duration: 500,
      easing: 'easeInOutQuad',
    },
    'plugins.datalabels': {
      anchor: 'end',
      align: 'end',
      color: '#49454E',
      font: {
        family: 'Mukta',
        weight: 600,
        size: fontSize,
      },
      formatter: (value) => value,
    },
    'plugins.tooltip': {
      enabled: false,
    },
    'plugins.legend': {
      display: false,
    },
    'scales.x': {
      grid: {
        display: false,
      },
      ticks: {
        color: '#7A757F',
        maxRotation: 45,
        minRotation: 45,
        font: {
          family: 'Mukta',
          weight: 400,
          size: fontSize,
        },
      },
    },
    'scales.y': {
      min: 0,
      max: maxValue + Math.ceil(maxValue * 0.25) + 5,
      display: false,
    },
  };

  return (
    <div className={styles['graph-container']}>
      <Chart
        chartType="bar"
        data={chartData}
        customOptions={chartOptions}
        containerClass={styles.container}
      />
    </div>
  );
};

GraphView.propTypes = {
  projectDetails: PropTypes.shape({
    overviewColumns: PropTypes.array.isRequired,
    overviewData: PropTypes.array.isRequired,
  }).isRequired,
};

export default GraphView;
